import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const memoryCalculatorComponent: ComponentDef = {
  id: 'memory-calculator',
  name: 'Memory Calculator',
  description: 'Estimate model weights, activations, KV cache, and optimizer-state memory.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 8192, step: 64 },
    { name: 'n_layers', label: 'Layers', type: 'number', default: 12, min: 1, max: 256, step: 1 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'batch_size', label: 'Batch size', type: 'number', default: 4, min: 1, max: 128, step: 1 },
    {
      name: 'mode',
      label: 'Mode',
      type: 'select',
      default: 'training',
      options: [
        { label: 'Training', value: 'training' },
        { label: 'Inference', value: 'inference' }
      ]
    },
    {
      name: 'precision',
      label: 'Precision',
      type: 'select',
      default: 'fp16',
      options: [
        { label: 'FP32', value: 'fp32' },
        { label: 'FP16', value: 'fp16' },
        { label: 'INT8', value: 'int8' },
        { label: 'INT4', value: 'int4' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_layers = Number(params.n_layers);
    const seq_len = Number(params.seq_len);
    const batch_size = Number(params.batch_size);
    const mode = String(params.mode);
    const precision = String(params.precision);

    const bytesPerParam = precision === 'fp32' ? 4 : precision === 'fp16' ? 2 : precision === 'int8' ? 1 : 0.5;
    const precisionLabel = precision === 'fp32' ? 'FP32 (4B)' : precision === 'fp16' ? 'FP16 (2B)' : precision === 'int8' ? 'INT8 (1B)' : 'INT4 (0.5B)';

    const g = new GraphBuilder();
    g.input('config', [n_layers, d_model]);

    g.scale('weights', 'config', `Weights ${precisionLabel}`);

    if (mode === 'training') {
      // Training: weights + activations + optimizer states (Adam: 2× FP32 per param)
      g.scale('activations', 'config', `Activations ${precisionLabel}`);
      g.cache('kv_cache', 'config', [batch_size, n_layers, seq_len, d_model]);
      g.scale('optimizer', 'config', `Adam States (FP32 ×2)`);
      g.scale('gradients', 'config', `Gradients ${precisionLabel}`);
      g.concat('parts', ['weights', 'activations', 'kv_cache', 'optimizer', 'gradients'], [5]);
      g.scale('total', 'parts', `Total ×${bytesPerParam}B/param`);
    } else {
      // Inference: weights + KV cache only (no optimizer, no gradients)
      g.cache('kv_cache', 'config', [batch_size, n_layers, seq_len, d_model]);
      g.scale('activations', 'config', `Activations ${precisionLabel}`);
      g.concat('parts', ['weights', 'kv_cache', 'activations'], [3]);
      g.scale('total', 'parts', `Total ×${bytesPerParam}B/param`);
    }

    g.output('out', 'total');
    return g.build();
  }
};
